<!-- Desktop banner; on phones the mobile banner replaces it when the brand has
     one (legacy parity). One <picture>, so the browser downloads only the file
     for the current width — two <img>s hidden by CSS fetched both (~2 MB). It is
     usually the page's largest paint, hence fetchpriority. A brand with only a
     mobile banner shows nothing on desktop: the 1px source stops the phone file
     downloading there, and .ui-hdr__banner--mob hides the box. -->
<picture *ngIf="MenuWebBanner || MobileMenuWebBanner">
  <source
    *ngIf="MobileMenuWebBanner"
    media="(max-width: 767.98px)"
    [attr.srcset]="MobileMenuWebBanner"
  />
  <source
    *ngIf="!MenuWebBanner"
    media="(min-width: 768px)"
    srcset="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
  />
  <img
    [src]="MenuWebBanner || MobileMenuWebBanner"
    alt=""
    fetchpriority="high"
    class="ui-hdr__banner"
    [class.is-broken]="bannerBroken"
    (error)="bannerBroken = true"
    (load)="bannerBroken = false"
    [class.ui-hdr__banner--mob]="!MenuWebBanner"
    [class.ui-hdr__banner--compact]="isMenuPage"
  />
</picture>

<!-- 1px sentinel: once it scrolls out of view the header is "stuck" and gets a shadow -->
<div class="ui-hdr__sentinel" #sentinel></div>

<header
  #bar
  class="ui-hdr"
  [class.is-stuck]="isStuck"
  [class.is-hidden]="isHidden"
>
  <div class="ui-hdr__bar">
    <!-- mobile: site menu trigger — always the side nav, on every page. The
         header never shows product categories; the shop owns those. -->
    <button
      type="button"
      class="ui-hdr__icon ui-hdr__icon--bare ui-hdr__mob"
      aria-label="Open menu"
      (click)="openSideMenu()"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 7h16M4 12h16M4 17h16" />
      </svg>
    </button>

    <!-- brand -->
    <a class="ui-hdr__brand" [href]="webLink">
      <img *ngIf="logo" [src]="logo" [alt]="brandName || 'Logo'" class="ui-hdr__logo" />
      <span class="ui-hdr__name ui-hdr__mob">{{ brandName }}</span>
    </a>

    <!-- desktop: search, centred between equal left / right columns -->
    <div class="ui-hdr__search ui-hdr__desk">
      <app-search *ngIf="showSearch"></app-search>
    </div>

    <div class="ui-hdr__spacer ui-hdr__mob"></div>

    <div class="ui-hdr__right">

    <!-- desktop: account -->
    <div
      class="ui-hdr__acct ui-hdr__desk"
      *ngIf="isLoggedIn"
      (click)="$event.stopPropagation()"
    >
      <button
        type="button"
        class="ui-btn ui-btn--ghost ui-hdr__acct-btn"
        [attr.aria-expanded]="accountOpen"
        (click)="accountOpen = !accountOpen"
      >
        <span class="ui-hdr__avatar" aria-hidden="true" *ngIf="showAvatar">{{ initials }}</span>
        <span class="ui-hdr__acct-name">{{ firstName }}</span>
        <svg
          class="ui-hdr__chev"
          [class.is-open]="accountOpen"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <div class="ui-card ui-hdr__menu" *ngIf="accountOpen">
        <div class="ui-hdr__menu-head">
          <b>{{ firstName }} {{ lastName }}</b>
        </div>
        <button
          type="button"
          class="ui-hdr__menu-item"
          (click)="go('updateaccount')"
        >
          Account
        </button>
        <button
          type="button"
          class="ui-hdr__menu-item"
          *ngIf="showRewards"
          (click)="go('join-reward')"
        >
          Rewards
        </button>
        <button
          type="button"
          class="ui-hdr__menu-item"
          *ngIf="ShowReferSection"
          (click)="go('refer')"
        >
          Refer a friend
        </button>
        <hr class="ui-hairline ui-hdr__menu-rule" />
        <button
          type="button"
          class="ui-hdr__menu-item ui-hdr__menu-item--danger"
          (click)="signOut()"
        >
          Sign out
        </button>
      </div>
    </div>

    <button
      type="button"
      class="ui-btn ui-btn--ghost ui-hdr__login ui-hdr__desk"
      *ngIf="!isLoggedIn && showLogin"
      (click)="go('login')"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-3.6 3.6-6 8-6s8 2.4 8 6" />
      </svg>
      {{ loginLabel }}
    </button>

    <!-- mobile: search -->
    <button
      type="button"
      class="ui-hdr__icon ui-hdr__mob"
      aria-label="Search"
      *ngIf="showSearch"
      (click)="showMobileSearch = true"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
    </button>

    <!-- cart -->
    <button
      type="button"
      class="ui-btn ui-hdr__cart"
      aria-label="View cart"
      aria-haspopup="dialog"
      [attr.aria-expanded]="cartOpen"
      *ngIf="showCart"
      (click)="openCart()"
    >
      <span class="ui-hdr__cart-pill">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M3 4h2l2.4 11h11l2-7H7" />
          <circle cx="9" cy="20" r="1.4" />
          <circle cx="17" cy="20" r="1.4" />
        </svg>
        <b>{{ cartCounter$ | async }}</b>
      </span>
      <span class="ui-hdr__cart-total">
        {{ cartTotal$ | async | currency: CurrencySymbol }}
      </span>
    </button>
    </div>
  </div>

  <!-- desktop: nav strip -->
  <nav class="ui-hdr__nav ui-hdr__desk" *ngIf="tiles.length" aria-label="Site">
    <button
      type="button"
      class="ui-hdr__tab"
      *ngFor="let tile of tiles"
      [class.is-active]="isActive(tile)"
      (click)="redirect(tile)"
    >
      {{ tile.Title }}
    </button>
  </nav>
</header>

<!-- Side drawer (mobile) -->
<div
  class="ui-hdr__scrim"
  *ngIf="sideMenu"
  [class.is-closing]="isClosing"
  (click)="closeSideMenu()"
>
  <aside
    class="ui-hdr__drawer"
    [class.is-closing]="isClosing"
    (click)="$event.stopPropagation()"
  >
    <div class="ui-hdr__drawer-brand">
      <img *ngIf="logo" [src]="logo" [alt]="brandName || 'Logo'" class="ui-hdr__logo" />
      <div class="ui-hdr__drawer-title">
        <b>{{ brandName }}</b>
        <small *ngIf="isLoggedIn">{{ firstName }} {{ lastName }}</small>
      </div>
      <button
        type="button"
        class="ui-hdr__close"
        aria-label="Close menu"
        (click)="closeSideMenu()"
      >
        &times;
      </button>
    </div>

    <div class="ui-hdr__drawer-list">
      <button
        type="button"
        class="ui-choice"
        *ngFor="let tile of tiles"
        [class.is-selected]="isActive(tile)"
        (click)="redirect(tile); closeSideMenu()"
      >
        <span class="ui-choice__check" *ngIf="isActive(tile)">&#10003;</span>
        <span class="ui-choice__label">{{ tile.Title }}</span>
      </button>
    </div>

    <hr class="ui-hairline" />

    <div class="ui-hdr__drawer-label">Your account</div>
    <div class="ui-hdr__drawer-list">
      <button
        type="button"
        class="ui-choice"
        [class.is-selected]="currentComponent == 'updateaccount'"
        (click)="go('updateaccount')"
      >
        <span class="ui-choice__check" *ngIf="currentComponent == 'updateaccount'"
          >&#10003;</span
        >
        <span class="ui-choice__label">Account</span>
      </button>
      <button
        type="button"
        class="ui-choice"
        *ngIf="isLoggedIn && showRewards"
        [class.is-selected]="currentComponent == 'join-reward'"
        (click)="go('join-reward')"
      >
        <span class="ui-choice__check" *ngIf="currentComponent == 'join-reward'"
          >&#10003;</span
        >
        <span class="ui-choice__label">Rewards</span>
      </button>
      <button
        type="button"
        class="ui-choice"
        *ngIf="ShowReferSection"
        [class.is-selected]="currentComponent == 'refer'"
        (click)="go('refer')"
      >
        <span class="ui-choice__check" *ngIf="currentComponent == 'refer'"
          >&#10003;</span
        >
        <span class="ui-choice__label">Refer a friend</span>
      </button>
      <button
        type="button"
        class="ui-btn ui-btn--block"
        *ngIf="!isLoggedIn && showLogin"
        (click)="go('login')"
      >
        {{ loginLabel == 'Login' ? 'Log in' : loginLabel }}
      </button>
    </div>

    <div class="ui-hdr__drawer-foot" *ngIf="isLoggedIn">
      <button type="button" class="ui-link ui-link--danger" (click)="signOut()">
        Sign out
      </button>
    </div>
  </aside>
</div>

<!-- Cart drawer: the header cart shows the cart (app-new-side-cart, with its
     own "Your Order" heading); its Checkout closes this and runs
     recommendations → checkout. Opens from the right, on every width. -->
<div
  class="ui-hdr__scrim"
  *ngIf="cartOpen"
  [class.is-closing]="cartClosing"
  (click)="closeCart()"
>
  <aside
    class="ui-hdr__drawer ui-hdr__drawer--cart"
    [class.is-closing]="cartClosing"
    role="dialog"
    aria-modal="true"
    aria-label="Your cart"
    (click)="$event.stopPropagation()"
  >
    <button
      type="button"
      class="ui-hdr__close ui-hdr__close--cart"
      aria-label="Close cart"
      (click)="closeCart()"
    >
      &times;
    </button>
    <!-- app-new-side-cart, loaded on demand (lazy-components.ts) -->
    <ng-container
      [appLazyComponent]="loadNewSideCart"
      [lazyInputs]="{ inDrawer: true }"
      (lazyCreated)="onCartCreated($event)"
    ></ng-container>
  </aside>
</div>

<!-- Mobile search -->
<div
  class="ui-hdr__scrim ui-hdr__scrim--search"
  *ngIf="showMobileSearch"
  (click)="showMobileSearch = false"
>
  <div class="ui-hdr__msearch" (click)="$event.stopPropagation()">
    <!-- the field carries its own Clear / Cancel add-on; results hang off
         this sheet (position: relative) -->
    <app-search [embedded]="true" (close)="showMobileSearch = false"></app-search>
  </div>
</div>
